// Personal weekly reading timetable for students
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  BellOff,
  BookOpen,
  CalendarDays,
  Check,
  Clock,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import api from "../../services/api";
import { TEST_REMINDER, TIMETABLE_UPDATED } from "../StudyReminders/StudyReminders";
import {
  disablePush,
  enablePush,
  needsHomeScreenInstall,
  pushSupported,
  sendTestPush,
  syncPushState,
} from "../../services/push";
import "./ReadingTimetable.css";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SUBJECTS = [
  "Mathematics",
  "English",
  "Basic Science",
  "Basic Technology",
  "Social Studies",
  "Civic Education",
  "Business Studies",
  "Agricultural Science",
  "Computer Studies",
  "Home Economics",
  "CRS / IRS",
  "French",
  "Yoruba / Igbo / Hausa",
  "Physical & Health Education",
  "Cultural & Creative Arts",
  "History",
];
// Stable subject colours (navy/gold family plus a few soft accents)
const PALETTE = ["#0B1B3F", "#D9950F", "#2A4F9E", "#12A866", "#B4458C", "#0E7C86", "#8A5A00", "#5B4FC4"];

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const duration = (b) => toMinutes(b.end) - toMinutes(b.start);
const formatTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
};
const formatMinutes = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h}h ${m}m` : `${h}h`;
};
const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, "");
  return fallback;
};

const subjectColor = (subject) => {
  let hash = 0;
  for (const ch of subject || "") hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};

const emptyForm = (day) => ({
  id: null,
  subject: "Mathematics",
  customSubject: "",
  topic: "",
  notes: "",
  days: [day],
  start: "17:00",
  end: "17:45",
});

const SessionForm = ({ initial, onCancel, onSave, saving, error }) => {
  const [form, setForm] = useState(initial);
  const editing = Boolean(initial.id);
  const isCustom = form.subject === "__custom";
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDay = (d) =>
    set({
      days: editing
        ? [d]
        : form.days.includes(d)
        ? form.days.filter((x) => x !== d)
        : [...form.days, d],
    });

  const minutes = form.start && form.end ? toMinutes(form.end) - toMinutes(form.start) : 0;
  const subject = isCustom ? form.customSubject.trim() : form.subject;
  const canSave = subject && form.days.length > 0 && minutes > 0 && !saving;

  return (
    <form
      className="rt-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) onSave({ ...form, subject });
      }}
    >
      <div className="rt-form-head">
        <h3>{editing ? "Edit study session" : "Add a study session"}</h3>
        <button type="button" className="rt-icon-btn" onClick={onCancel} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div className="rt-field">
        <label htmlFor="rt-subject">Subject</label>
        <select
          id="rt-subject"
          value={form.subject}
          onChange={(e) => set({ subject: e.target.value })}
        >
          {SUBJECTS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          <option value="__custom">Something else…</option>
        </select>
        {isCustom && (
          <input
            className="rt-input"
            placeholder="Type the subject"
            maxLength={60}
            value={form.customSubject}
            onChange={(e) => set({ customSubject: e.target.value })}
            autoFocus
          />
        )}
      </div>

      <div className="rt-field">
        <label htmlFor="rt-topic">
          Topic <span className="rt-optional">(optional)</span>
        </label>
        <input
          id="rt-topic"
          className="rt-input"
          placeholder="e.g. Fractions, Parts of speech"
          maxLength={120}
          value={form.topic}
          onChange={(e) => set({ topic: e.target.value })}
        />
      </div>

      <div className="rt-field">
        <span className="rt-label" id="rt-days-label">
          {editing ? "Day" : "Days"}
        </span>
        <div className="rt-days" role="group" aria-labelledby="rt-days-label">
          {DAY_SHORT.map((d, i) => (
            <button
              key={d}
              type="button"
              className={`rt-day-chip ${form.days.includes(i) ? "selected" : ""}`}
              aria-pressed={form.days.includes(i)}
              onClick={() => toggleDay(i)}
            >
              {d}
            </button>
          ))}
        </div>
        {!editing && <small className="rt-hint">Pick every day you want to repeat this session.</small>}
      </div>

      <div className="rt-time-row">
        <div className="rt-field">
          <label htmlFor="rt-start">Start</label>
          <input id="rt-start" type="time" className="rt-input" value={form.start} onChange={(e) => set({ start: e.target.value })} required />
        </div>
        <div className="rt-field">
          <label htmlFor="rt-end">End</label>
          <input id="rt-end" type="time" className="rt-input" value={form.end} onChange={(e) => set({ end: e.target.value })} required />
        </div>
        <div className="rt-duration" aria-live="polite">
          {minutes > 0 ? formatMinutes(minutes) : "—"}
        </div>
      </div>

      <div className="rt-field">
        <label htmlFor="rt-notes">
          Notes <span className="rt-optional">(optional)</span>
        </label>
        <input
          id="rt-notes"
          className="rt-input"
          placeholder="e.g. Do exercise 3B, revise with a friend"
          maxLength={300}
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </div>

      {error && <div className="rt-error" role="alert">{error}</div>}

      <div className="rt-form-actions">
        <button type="button" className="eg-btn eg-btn-outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="eg-btn eg-btn-primary" disabled={!canSave}>
          {saving ? "Saving…" : editing ? "Save changes" : "Add to timetable"}
        </button>
      </div>
    </form>
  );
};

const SessionCard = ({ block, done, isToday, isPast, onToggle, onEdit, onDelete, onStudy, compact }) => (
  <div
    className={`rt-session ${done ? "done" : ""} ${compact ? "compact" : ""}`}
    style={{ "--rt-color": subjectColor(block.subject) }}
  >
    <div className="rt-session-main">
      <span className="rt-session-time">
        {formatTime(block.start)} – {formatTime(block.end)}
      </span>
      <strong className="rt-session-subject">{block.subject}</strong>
      {block.topic && <span className="rt-session-topic">{block.topic}</span>}
      {!compact && block.notes && <span className="rt-session-notes">{block.notes}</span>}
    </div>
    <div className="rt-session-actions">
      {(isToday || isPast) && (
        <button
          type="button"
          className={`rt-check ${done ? "checked" : ""}`}
          onClick={onToggle}
          aria-pressed={done}
          title={done ? "Mark as not done" : "Mark as done"}
        >
          <Check size={14} />
          <span className="rt-sr">{done ? "Done" : "Mark done"}</span>
        </button>
      )}
      {!compact && isToday && !done && (
        <button type="button" className="rt-study-btn" onClick={onStudy}>
          <BookOpen size={14} /> Study now
        </button>
      )}
      <button type="button" className="rt-icon-btn" onClick={onEdit} aria-label={`Edit ${block.subject}`}>
        <Pencil size={14} />
      </button>
      <button type="button" className="rt-icon-btn danger" onClick={onDelete} aria-label={`Delete ${block.subject}`}>
        <Trash2 size={14} />
      </button>
    </div>
  </div>
);

const notificationPermission = () =>
  typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported";

const ClosedBrowserReminders = ({ onPermission }) => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const supported = pushSupported();
  const iosInstall = supported && needsHomeScreenInstall();

  useEffect(() => {
    syncPushState().then(setOn).catch(() => setOn(false));
  }, []);

  const toggle = async () => {
    setBusy(true);
    setMessage(null);
    try {
      if (on) {
        await disablePush();
        setOn(false);
        setMessage({ ok: true, text: "Closed-browser reminders turned off on this device." });
      } else {
        await enablePush();
        setOn(true);
        setMessage({ ok: true, text: "Done! You'll get reminders on this device even when MyEduGenius is closed." });
      }
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.detail || err.message || "Something went wrong." });
    } finally {
      onPermission?.(notificationPermission());
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await sendTestPush();
      setMessage({ ok: true, text: "Test sent — it should pop up in a few seconds." });
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.detail || "Couldn't send a test." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rt-push">
      <div className="rt-push-text">
        <strong>Reminders when the browser is closed</strong>
        <small>
          {!supported
            ? "This browser doesn't support this. Try Chrome, Edge or Firefox."
            : iosInstall
            ? "On iPhone/iPad: tap Share → Add to Home Screen, open MyEduGenius from the Home Screen, then turn this on."
            : on
            ? "On for this device. Turn it on for each phone or computer you use."
            : "Get study reminders on this device even when MyEduGenius isn't open."}
        </small>
        {message && <span className={`rt-push-msg ${message.ok ? "ok" : "err"}`}>{message.text}</span>}
      </div>
      {supported && !iosInstall && (
        <div className="rt-push-actions">
          {on && (
            <button type="button" className="rt-link-btn rt-test-btn" onClick={test} disabled={busy}>
              Send test
            </button>
          )}
          <button type="button" className={`eg-btn ${on ? "eg-btn-outline" : "eg-btn-primary"} rt-small-btn`} onClick={toggle} disabled={busy}>
            {busy ? "Please wait…" : on ? "Turn off" : "Turn on"}
          </button>
        </div>
      )}
    </div>
  );
};

const RemindersCard = ({ reminders, onChange, saving }) => {
  const [permission, setPermission] = useState(notificationPermission());

  const allow = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      setPermission(notificationPermission());
    }
  };

  const update = (patch) => onChange({ ...reminders, ...patch });

  return (
    <div className="dashboard-section rt-reminders">
      <div className="rt-card-head">
        <h3>
          {reminders.enabled ? <Bell size={18} /> : <BellOff size={18} />} Study reminders
        </h3>
        <label className="rt-switch">
          <input
            type="checkbox"
            checked={reminders.enabled}
            disabled={saving}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <span aria-hidden="true" />
          <span className="rt-sr">Turn study reminders {reminders.enabled ? "off" : "on"}</span>
        </label>
      </div>

      {reminders.enabled ? (
        <div className="rt-reminder-grid">
          <div className="rt-field">
            <label htmlFor="rt-lead">Remind me</label>
            <select
              id="rt-lead"
              value={reminders.minutes_before}
              disabled={saving}
              onChange={(e) => update({ minutes_before: Number(e.target.value) })}
            >
              <option value={0}>When the session starts</option>
              <option value={5}>5 minutes before</option>
              <option value={10}>10 minutes before</option>
              <option value={15}>15 minutes before</option>
              <option value={30}>30 minutes before</option>
            </select>
          </div>

          <label className="rt-checkbox">
            <input
              type="checkbox"
              checked={reminders.end_prompt}
              disabled={saving}
              onChange={(e) => update({ end_prompt: e.target.checked })}
            />
            <span>
              <strong>Time&apos;s-up check-in</strong>
              <small>When a session ends, ask me if I finished so I can tick it off.</small>
            </span>
          </label>

          <div className="rt-permission">
            {permission === "granted" && (
              <span className="rt-perm ok">
                ✓ Pop-up notifications are on, even when MyEduGenius is in the background.
              </span>
            )}
            {permission === "default" && (
              <>
                <span className="rt-perm">Get a pop-up even when you&apos;re on another tab or app.</span>
                <button type="button" className="eg-btn eg-btn-outline rt-small-btn" onClick={allow}>
                  <Bell size={14} /> Allow notifications
                </button>
              </>
            )}
            {permission === "denied" && (
              <span className="rt-perm warn">
                Notifications are blocked for this site. To turn them on, click the lock icon next to the web
                address and allow Notifications. Reminders will still appear inside MyEduGenius.
              </span>
            )}
            {permission === "unsupported" && (
              <span className="rt-perm">
                This browser can&apos;t show pop-ups; reminders will appear inside MyEduGenius.
              </span>
            )}
            <button
              type="button"
              className="rt-link-btn rt-test-btn"
              onClick={() => window.dispatchEvent(new Event(TEST_REMINDER))}
            >
              Send a test reminder
            </button>
          </div>

          <ClosedBrowserReminders onPermission={setPermission} />
        </div>
      ) : (
        <p className="rt-muted">Turn on reminders to get a nudge before each study session.</p>
      )}
    </div>
  );
};

const ReadingTimetable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null); // form initial values when open
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/timetable");
      setData(res.data);
      setLoadError(null);
      setSelectedDay((d) => (d === null ? res.data.today.day : d));
    } catch (err) {
      setLoadError(errorText(err, "Couldn't load your timetable. Please try again."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (text) => {
    setNotice(text);
    setTimeout(() => setNotice(null), 2500);
  };

  const run = async (request, message) => {
    try {
      const res = await request();
      setData(res.data);
      window.dispatchEvent(new Event(TIMETABLE_UPDATED));
      if (message) flash(message);
      return true;
    } catch (err) {
      flash(errorText(err, "Something went wrong. Please try again."));
      return false;
    }
  };

  const save = async (values) => {
    setSaving(true);
    setFormError(null);
    const payload = {
      subject: values.subject,
      topic: values.topic || null,
      notes: values.notes || null,
      start: values.start,
      end: values.end,
    };
    try {
      const res = values.id
        ? await api.put(`/api/timetable/blocks/${values.id}`, { ...payload, day: values.days[0] })
        : await api.post("/api/timetable/blocks", { ...payload, days: values.days });
      setData(res.data);
      window.dispatchEvent(new Event(TIMETABLE_UPDATED));
      setForm(null);
      flash(values.id ? "Session updated" : values.days.length > 1 ? `Added ${values.days.length} sessions` : "Session added");
    } catch (err) {
      setFormError(errorText(err, "Couldn't save that session."));
    } finally {
      setSaving(false);
    }
  };

  const byDay = useMemo(() => {
    const days = Array.from({ length: 7 }, () => []);
    (data?.blocks || []).forEach((b) => days[b.day].push(b));
    return days;
  }, [data]);

  if (loadError) {
    return (
      <div className="dashboard-section rt-empty">
        <p>{loadError}</p>
        <button type="button" className="eg-btn eg-btn-primary" onClick={load}>
          Try again
        </button>
      </div>
    );
  }
  if (!data) return <div className="dashboard-section rt-loading">Loading your timetable…</div>;

  const { week, today, completions, stats } = data;
  const isDone = (block) => (completions[week[block.day].date] || []).includes(block.id);
  const toggle = (block) =>
    run(
      () => api.post(`/api/timetable/blocks/${block.id}/done`, { date: week[block.day].date, done: !isDone(block) }),
      isDone(block) ? null : "Nice work! Session ticked off ✓"
    );
  const openEdit = (b) => {
    setFormError(null);
    const known = SUBJECTS.includes(b.subject);
    setForm({
      id: b.id,
      subject: known ? b.subject : "__custom",
      customSubject: known ? "" : b.subject,
      topic: b.topic || "",
      notes: b.notes || "",
      days: [b.day],
      start: b.start,
      end: b.end,
    });
  };
  const remove = (b) => run(() => api.delete(`/api/timetable/blocks/${b.id}`), "Session removed");
  const study = (b) => navigate("/agents/tutor", { state: { context: { subject: b.subject, topic: b.topic } } });

  const nowMinutes = (() => {
    const lagos = new Date(new Date().toLocaleString("en-US", { timeZone: "Africa/Lagos" }));
    return lagos.getHours() * 60 + lagos.getMinutes();
  })();
  const todays = byDay[today.day];
  const nextUp = todays.find((b) => toMinutes(b.end) > nowMinutes && !isDone(b));
  const progress = stats.planned_sessions ? Math.round((stats.done_sessions / stats.planned_sessions) * 100) : 0;
  const empty = data.blocks.length === 0;

  const cardProps = (b) => ({
    block: b,
    done: isDone(b),
    isToday: b.day === today.day,
    isPast: b.day < today.day,
    onToggle: () => toggle(b),
    onEdit: () => openEdit(b),
    onDelete: () => remove(b),
    onStudy: () => study(b),
  });

  return (
    <div className="rt">
      {notice && <div className="rt-toast" role="status">{notice}</div>}

      {/* Summary row */}
      <div className="rt-summary">
        <div className="dashboard-section rt-today">
          <div className="rt-card-head">
            <h3>
              <CalendarDays size={18} /> Today · {week[today.day].name}
            </h3>
            <button
              type="button"
              className="eg-btn eg-btn-accent rt-add-btn"
              onClick={() => {
                setFormError(null);
                setForm(emptyForm(today.day));
              }}
            >
              <Plus size={16} /> Add session
            </button>
          </div>
          {nextUp && (
            <div className="rt-next" style={{ "--rt-color": subjectColor(nextUp.subject) }}>
              <span className="rt-next-label">
                <Clock size={14} /> {toMinutes(nextUp.start) <= nowMinutes ? "Happening now" : "Next up"}
              </span>
              <strong>{nextUp.subject}</strong>
              <span>
                {formatTime(nextUp.start)} – {formatTime(nextUp.end)}
                {nextUp.topic ? ` · ${nextUp.topic}` : ""}
              </span>
            </div>
          )}
          {todays.length ? (
            <div className="rt-list">
              {todays.map((b) => (
                <SessionCard key={b.id} {...cardProps(b)} />
              ))}
            </div>
          ) : (
            <p className="rt-muted">Nothing planned for today{empty ? "" : " — enjoy your break!"}</p>
          )}
        </div>

        <div className="dashboard-section rt-progress">
          <h3>This week</h3>
          <div
            className="rt-ring"
            style={{ "--rt-progress": `${progress * 3.6}deg` }}
            role="img"
            aria-label={`${stats.done_sessions} of ${stats.planned_sessions} sessions done this week`}
          >
            <span>{progress}%</span>
          </div>
          <p>
            <strong>
              {stats.done_sessions} of {stats.planned_sessions}
            </strong>{" "}
            sessions done
          </p>
          <p className="rt-muted">
            {formatMinutes(stats.done_minutes)} of {formatMinutes(stats.planned_minutes)} planned
          </p>
        </div>
      </div>

      <RemindersCard
        reminders={data.reminders || { enabled: true, minutes_before: 5, end_prompt: true }}
        saving={savingReminders}
        onChange={async (next) => {
          setSavingReminders(true);
          await run(
            () => api.put("/api/timetable/reminders", next),
            next.enabled ? "Reminder settings saved" : "Reminders turned off"
          );
          setSavingReminders(false);
        }}
      />

      {/* Empty state with starter plan */}
      {empty && !form && (
        <div className="dashboard-section rt-empty">
          <Sparkles size={28} className="rt-empty-icon" />
          <h3>Plan your reading week</h3>
          <p>
            Add study sessions for the days and times that suit you, or start with a simple plan: 45 minutes
            each weekday at 5pm, rotating your core subjects. You can change it any time.
          </p>
          <div className="rt-empty-actions">
            <button type="button" className="eg-btn eg-btn-accent" onClick={() => run(() => api.post("/api/timetable/starter", {}), "Starter plan added — edit it to suit you")}>
              <Sparkles size={16} /> Use a starter plan
            </button>
            <button type="button" className="eg-btn eg-btn-outline" onClick={() => setForm(emptyForm(today.day))}>
              <Plus size={16} /> Add my own
            </button>
          </div>
        </div>
      )}

      {form && (
        <div className="dashboard-section">
          <SessionForm
            key={form.id || "new"}
            initial={form}
            saving={saving}
            error={formError}
            onCancel={() => setForm(null)}
            onSave={save}
          />
        </div>
      )}

      {/* Week grid (desktop) */}
      {!empty && (
        <div className="dashboard-section rt-week">
          <div className="rt-card-head">
            <h3>Weekly timetable</h3>
            {confirmClear ? (
              <div className="rt-confirm">
                <span>Remove every session?</span>
                <button type="button" className="eg-btn eg-btn-outline" onClick={() => setConfirmClear(false)}>
                  Keep
                </button>
                <button
                  type="button"
                  className="eg-btn rt-danger-btn"
                  onClick={async () => {
                    await run(() => api.delete("/api/timetable"), "Timetable cleared");
                    setConfirmClear(false);
                  }}
                >
                  Clear all
                </button>
              </div>
            ) : (
              <button type="button" className="rt-link-btn" onClick={() => setConfirmClear(true)}>
                Clear timetable
              </button>
            )}
          </div>

          {/* Mobile day picker */}
          <div className="rt-day-tabs" role="tablist" aria-label="Choose a day">
            {week.map((d) => (
              <button
                key={d.day}
                type="button"
                role="tab"
                aria-selected={selectedDay === d.day}
                className={`rt-day-tab ${selectedDay === d.day ? "active" : ""} ${d.day === today.day ? "today" : ""}`}
                onClick={() => setSelectedDay(d.day)}
              >
                <span>{DAY_SHORT[d.day]}</span>
                <small>{byDay[d.day].length || "–"}</small>
              </button>
            ))}
          </div>

          <div className="rt-grid">
            {week.map((d) => (
              <div
                key={d.day}
                className={`rt-col ${d.day === today.day ? "today" : ""} ${selectedDay === d.day ? "selected" : ""}`}
              >
                <div className="rt-col-head">
                  <span>{DAY_SHORT[d.day]}</span>
                  <small>{new Date(`${d.date}T12:00:00`).getDate()}</small>
                </div>
                <div className="rt-col-body">
                  {byDay[d.day].map((b) => (
                    <SessionCard key={b.id} compact {...cardProps(b)} />
                  ))}
                  <button
                    type="button"
                    className="rt-add-slot"
                    onClick={() => {
                      setFormError(null);
                      setForm(emptyForm(d.day));
                    }}
                    aria-label={`Add a session on ${d.name}`}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingTimetable;
